/**
 * Theme Defaults and Helpers
 * Provides preset themes, font options, and CSS variable generation
 */

// Theme presets - users can select dark or light as a base
export const themePresets = {
  dark: {
    accent: '#749555',
    pageBackground: '#0d0d0d',
    sectionBackground: '#1a1a2e',
    cardBackground: '#1e1e2e',
    headingColor: '#ffffff',
    bodyColor: '#ffffff',
    mutedColor: '#94a3b8',
    buttonTextColor: null,
  },
  light: {
    accent: '#749555',
    pageBackground: '#ffffff',
    sectionBackground: '#f8fafc',
    cardBackground: '#ffffff',
    headingColor: '#1a1a2e',
    bodyColor: '#374151',
    mutedColor: '#6b7280',
    buttonTextColor: null,
  },
};

// Font options for typography customization.
// `weights` lists the weights actually shipped by Google Fonts for that family.
// Requesting weights a font doesn't have returns HTTP 400 → font never loads.
export const fontOptions = [
  // Sans-serif
  { value: 'Inter', label: 'Inter (Default)', category: 'sans-serif', weights: ['400', '500', '600', '700'] },
  { value: 'Poppins', label: 'Poppins', category: 'sans-serif', weights: ['400', '500', '600', '700'] },
  { value: 'Montserrat', label: 'Montserrat', category: 'sans-serif', weights: ['400', '500', '600', '700'] },
  { value: 'Roboto', label: 'Roboto', category: 'sans-serif', weights: ['400', '500', '700'] },
  { value: 'Open Sans', label: 'Open Sans', category: 'sans-serif', weights: ['400', '500', '600', '700'] },
  { value: 'Genty Sans', label: 'Genty Sans', category: 'sans-serif', isLocal: true, weights: ['400', '700'] },
  // Serif
  { value: 'Playfair Display', label: 'Playfair Display', category: 'serif', weights: ['400', '500', '600', '700'] },
  { value: 'Merriweather', label: 'Merriweather', category: 'serif', weights: ['400', '700'] },
  { value: 'Lora', label: 'Lora', category: 'serif', weights: ['400', '500', '600', '700'] },
  // Display & Fun
  { value: 'Bebas Neue', label: 'Bebas Neue', category: 'display', weights: ['400'] },
  { value: 'Righteous', label: 'Righteous', category: 'display', weights: ['400'] },
  { value: 'Fredoka', label: 'Fredoka', category: 'display', weights: ['400', '500', '600', '700'] },
  { value: 'Bangers', label: 'Bangers', category: 'display', weights: ['400'] },
  { value: 'Genty', label: 'Genty', category: 'display', isLocal: true, weights: ['400', '700'] },
  { value: 'Permanent Marker', label: 'Permanent Marker', category: 'handwriting', weights: ['400'] },
  { value: 'Caveat', label: 'Caveat', category: 'handwriting', weights: ['400', '500', '600', '700'] },
  { value: 'Pacifico', label: 'Pacifico', category: 'handwriting', weights: ['400'] },
  { value: 'Press Start 2P', label: 'Press Start 2P', category: 'display', weights: ['400'] },
];

// Lookup: font value → available weights
const FONT_WEIGHTS = Object.fromEntries(
  fontOptions.map((f) => [f.value, f.weights || ['400', '700']])
);

// Locally-hosted fonts that should NOT be requested from Google Fonts.
// These are loaded via @font-face in src/index.css.
export const LOCAL_FONTS = new Set(
  fontOptions.filter((f) => f.isLocal).map((f) => f.value)
);

// Gradient direction options
export const gradientDirections = [
  { value: '90deg', label: '→', description: 'Left to Right' },
  { value: '135deg', label: '↘', description: 'Diagonal Down' },
  { value: '180deg', label: '↓', description: 'Top to Bottom' },
  { value: '225deg', label: '↙', description: 'Diagonal Down Left' },
  { value: '270deg', label: '←', description: 'Right to Left' },
  { value: '45deg', label: '↗', description: 'Diagonal Up' },
];

/**
 * Get default theme colors based on preset
 * @param {string} preset - 'dark' or 'light'
 * @returns {object} Color values
 */
export const getPresetColors = (preset = 'dark') => {
  return themePresets[preset] || themePresets.dark;
};

/**
 * Merge user theme with preset defaults
 * @param {object} theme - User's theme settings
 * @returns {object} Complete theme with all values
 */
export const getResolvedTheme = (theme = {}) => {
  const preset = theme.preset || 'dark';
  const presetColors = getPresetColors(preset);

  return {
    preset,
    colors: {
      accent: theme.colors?.accent || theme.primaryColor || presetColors.accent,
      accentGradient: theme.colors?.accentGradient || null,
      pageBackground: theme.colors?.pageBackground || presetColors.pageBackground,
      sectionBackground: theme.colors?.sectionBackground || presetColors.sectionBackground,
      cardBackground: theme.colors?.cardBackground || presetColors.cardBackground,
      headingColor: theme.colors?.headingColor || presetColors.headingColor,
      bodyColor: theme.colors?.bodyColor || presetColors.bodyColor,
      mutedColor: theme.colors?.mutedColor || presetColors.mutedColor,
      buttonTextColor: theme.colors?.buttonTextColor || null,
    },
    fonts: {
      heading: theme.fonts?.heading || 'Inter',
      body: theme.fonts?.body || 'Inter',
    },
    // Keep original fields for compatibility
    primaryColor: theme.primaryColor || presetColors.accent,
    layout: theme.layout || 'linkblock',
    profileType: theme.profileType || '',
    headerImage: theme.headerImage || '',
  };
};

/**
 * Generate CSS custom properties from theme
 * @param {object} theme - Theme settings
 * @returns {object} CSS variable object for style prop
 */
export const getThemeCSS = (theme = {}) => {
  const resolved = getResolvedTheme(theme);

  return {
    '--theme-accent': resolved.colors.accent,
    '--theme-accent-hover': adjustColor(resolved.colors.accent, -15),
    '--theme-bg-page': resolved.colors.pageBackground,
    '--theme-bg-section': resolved.colors.sectionBackground,
    '--theme-bg-card': resolved.colors.cardBackground,
    '--theme-text-heading': resolved.colors.headingColor,
    '--theme-text-body': resolved.colors.bodyColor,
    '--theme-text-muted': resolved.colors.mutedColor,
    '--theme-font-heading': `"${resolved.fonts.heading}", system-ui, sans-serif`,
    '--theme-font-body': `"${resolved.fonts.body}", system-ui, sans-serif`,
    // Border colors derived from background
    '--theme-border': resolved.preset === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.1)',
    '--theme-border-hover': resolved.preset === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.2)',
  };
};

/**
 * Get accent style object (supports gradient)
 * @param {object} theme - Theme settings
 * @returns {object} Style object for background
 */
export const getAccentStyle = (theme = {}) => {
  const resolved = getResolvedTheme(theme);

  if (resolved.colors.accentGradient) {
    return { background: resolved.colors.accentGradient };
  }

  return { backgroundColor: resolved.colors.accent };
};

/**
 * Get accent gradient or solid color CSS value
 * @param {object} theme - Theme settings
 * @returns {string} CSS background value
 */
export const getAccentBackground = (theme = {}) => {
  const resolved = getResolvedTheme(theme);

  if (resolved.colors.accentGradient) {
    return resolved.colors.accentGradient;
  }

  return resolved.colors.accent;
};

/**
 * Create a gradient from two colors
 * @param {string} color1 - Start color (hex)
 * @param {string} color2 - End color (hex)
 * @param {string} direction - Gradient direction in degrees
 * @returns {string} CSS linear-gradient value
 */
export const createGradient = (color1, color2, direction = '135deg') => {
  return `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;
};

/**
 * Adjust a hex color brightness
 * @param {string} hex - Hex color value
 * @param {number} percent - Percentage to adjust (-100 to 100)
 * @returns {string} Adjusted hex color
 */
export const adjustColor = (hex, percent) => {
  if (!hex || !hex.startsWith('#')) return hex;

  const num = parseInt(hex.slice(1), 16);
  const amt = Math.round(2.55 * percent);

  const R = Math.max(0, Math.min(255, (num >> 16) + amt));
  const G = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + amt));
  const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));

  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

/**
 * Check if a color is light or dark
 * @param {string} hex - Hex color value
 * @returns {boolean} True if color is light
 */
export const isLightColor = (hex) => {
  if (!hex || !hex.startsWith('#')) return false;

  const num = parseInt(hex.slice(1), 16);
  const R = num >> 16;
  const G = (num >> 8) & 0x00FF;
  const B = num & 0x0000FF;

  // Using relative luminance formula
  const luminance = (0.299 * R + 0.587 * G + 0.114 * B) / 255;
  return luminance > 0.5;
};

/**
 * Get Google Fonts URL for selected fonts
 * @param {object} theme - Theme settings
 * @returns {string} Google Fonts URL
 */
export const getGoogleFontsUrl = (theme = {}) => {
  const resolved = getResolvedTheme(theme);
  const fonts = new Set([resolved.fonts.heading, resolved.fonts.body]);

  // Remove default font if it matches system font
  fonts.delete('inherit');
  fonts.delete('system-ui');

  // Remove locally-hosted custom fonts (loaded via @font-face in index.css)
  for (const local of LOCAL_FONTS) {
    fonts.delete(local);
  }

  if (fonts.size === 0) return null;

  const fontFamilies = Array.from(fonts)
    .map((font) => {
      const weights = (FONT_WEIGHTS[font] || ['400', '700']).join(';');
      return `family=${font.replace(/ /g, '+')}:wght@${weights}`;
    })
    .join('&');

  return `https://fonts.googleapis.com/css2?${fontFamilies}&display=swap`;
};

/**
 * Build a Google Fonts URL for an arbitrary list of font names.
 * Filters out system fonts, local fonts (@font-face), duplicates,
 * and honors each font's actual available weights.
 */
export const buildGoogleFontsUrl = (fontNames = []) => {
  const fonts = new Set(fontNames.filter(Boolean));
  fonts.delete('inherit');
  fonts.delete('system-ui');
  for (const local of LOCAL_FONTS) {
    fonts.delete(local);
  }
  if (fonts.size === 0) return null;
  const families = Array.from(fonts)
    .map((font) => {
      const weights = (FONT_WEIGHTS[font] || ['400', '700']).join(';');
      return `family=${font.replace(/ /g, '+')}:wght@${weights}`;
    })
    .join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
};

/**
 * Get a Google Fonts URL that preloads every option in fontOptions.
 * Used by the font picker UI so the dropdown and preview text can render
 * any selectable font without waiting for a fetch on each switch.
 */
export const getAllFontsPreviewUrl = () => {
  const families = fontOptions
    .filter((f) => !f.isLocal)
    .map((f) => {
      const weights = (f.weights || ['400', '700']).join(';');
      return `family=${f.value.replace(/ /g, '+')}:wght@${weights}`;
    })
    .join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
};

/**
 * Default theme object for new profiles
 */
export const defaultTheme = {
  preset: 'dark',
  primaryColor: '#749555',
  layout: 'linkblock',
  profileType: '',
  headerImage: '',
  colors: {
    accent: '#749555',
    accentGradient: null,
    pageBackground: '#0d0d0d',
    sectionBackground: '#1a1a2e',
    cardBackground: '#1e1e2e',
    headingColor: '#ffffff',
    bodyColor: '#ffffff',
    mutedColor: '#94a3b8',
  },
  fonts: {
    heading: 'Inter',
    body: 'Inter',
  },
};
